import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, CheckCircle2, Send, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import Seo, { organizationSchema } from "@/lib/seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { IMAGES } from "@/data/site";
import { SERVICES } from "@/data/services";
import { useSubmitEnquiry, useSite } from "@/hooks/content";
import SocialLinks from "@/components/SocialLinks";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  company: z.string().optional(),
  mobile: z.string().min(6, "Please enter a valid mobile number"),
  email: z.string().email("Please enter a valid email address"),
  service: z.string().min(1, "Please select a service"),
  message: z.string().min(10, "Please tell us a little more (min 10 characters)"),
  consent: z.literal(true, { errorMap: () => ({ message: "Please provide your consent to proceed" }) }),
});

// Built per render rather than at module load: the number and email are
// editable in the admin panel, so they are only known once settings resolve.
const buildContactCards = (site, telLink, waLink) => [
  { icon: Phone, label: "Call Us", value: site.mobile, href: telLink, testid: "contact-call" },
  { icon: MessageCircle, label: "WhatsApp", value: site.mobile, href: waLink(), testid: "contact-whatsapp", external: true },
  { icon: Mail, label: "Email Us", value: site.email, href: `mailto:${site.email}`, testid: "contact-email" },
];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-navy outline-none transition-all placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/20";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const {
    register, handleSubmit, reset, watch,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { service: "", consent: false } });

  const consent = watch("consent");
  const submit = useSubmitEnquiry();
  const { site, socials, waLink, telLink } = useSite();
  const contactCards = buildContactCards(site, telLink, waLink);

  const onSubmit = async (data) => {
    try {
      await submit.mutateAsync(data);
      setSent(true);
      reset();
      toast.success("Enquiry received", { description: "Thank you — SKY will be in touch shortly." });
    } catch (err) {
      // The enquiry was not stored. Keep the user's input on screen so nothing
      // is retyped, and point them at WhatsApp as an immediate alternative.
      toast.error("We couldn't send your enquiry", {
        description: "Please try again, or message us on WhatsApp.",
      });
    }
  };

  return (
    <>
      <Seo
        title="Contact Us"
        description="Contact SKY Training & Consultancy for workplace safety, fire safety, emergency preparedness or training enquiries in Singapore. Call, WhatsApp, email or send an enquiry."
        path="/contact"
        schema={organizationSchema(site)}
      />
      <PageHero
        eyebrow="Contact"
        title="Let's Make Your Workplace Safer"
        description="Speak with SKY about your organisation's safety, compliance, emergency preparedness or training requirements."
        image={IMAGES.consultantMeeting}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />

      <section className="section-pad">
        <div className="container-sky grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
          {/* Left: details */}
          <div>
            <Reveal>
              <h2 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">Get in Touch</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {site.name}<br />UEN: {site.uen}
              </p>
            </Reveal>

            <div className="mt-8 space-y-4">
              {contactCards.map((c) => (
                <Reveal key={c.label} delay={0.05}>
                  <a
                    href={c.href}
                    data-testid={c.testid}
                    {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="group flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-5 transition-all hover:border-brand/30 hover:shadow-md"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block font-mono-tech text-[10px] uppercase tracking-wider text-slate-400">{c.label}</span>
                      <span className="block text-sm font-bold text-navy">{c.value}</span>
                    </span>
                    <ArrowUpRight className="ml-auto h-4 w-4 text-slate-300 transition-colors group-hover:text-brand" />
                  </a>
                </Reveal>
              ))}
            </div>

            {socials.length > 0 && (
              <Reveal delay={0.08}>
                <div className="mt-6">
                  <p className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-400">Follow Us</p>
                  <SocialLinks socials={socials} tone="light" className="mt-3" />
                </div>
              </Reveal>
            )}

            {/* Map / location placeholder — address to be added later */}
            <Reveal delay={0.1}>
              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-100 bg-white">
                <div className="relative flex h-48 items-center justify-center bg-gradient-to-br from-navy to-navy-deep">
                  <div className="pointer-events-none absolute inset-0 bg-blueprint-dark opacity-30" />
                  <div className="relative text-center">
                    <MapPin className="mx-auto h-8 w-8 text-brand-light" />
                    <p className="mt-2 text-sm font-semibold text-white">Serving organisations across Singapore</p>
                    <p className="mt-1 text-xs text-slate-400">Location map can be added here.</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.15}>
            <div className="relative rounded-[1.8rem] border border-slate-100 bg-white p-7 shadow-[0_12px_48px_rgba(3,56,106,0.08)] sm:p-10">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                    data-testid="contact-success"
                  >
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand/10 text-brand">
                      <CheckCircle2 className="h-10 w-10" />
                    </span>
                    <h3 className="mt-6 font-heading text-2xl font-bold text-navy">Enquiry Received</h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                      Thank you for reaching out to SKY. We'll review your enquiry and get back to you shortly.
                      For an immediate response, message us on WhatsApp.
                    </p>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                      <button onClick={() => setSent(false)} className="btn-outline-sky">Send Another Enquiry</button>
                      <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                        <MessageCircle className="h-4 w-4" /> WhatsApp Us
                      </a>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit(onSubmit)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="contact-quote-form"
                    noValidate
                  >
                    <h2 className="font-heading text-2xl font-extrabold text-navy">Send an Enquiry</h2>
                    <p className="mt-2 text-sm text-slate-500">Tell us about your requirements and we'll be in touch.</p>

                    <div className="mt-7 grid gap-5 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Name *</label>
                        <input {...register("name")} data-testid="contact-name" className={inputCls} placeholder="Your full name" />
                        {errors.name && <p className="mt-1.5 text-xs text-red-500">{errors.name.message}</p>}
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Company</label>
                        <input {...register("company")} data-testid="contact-company" className={inputCls} placeholder="Your organisation" />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Mobile *</label>
                        <input {...register("mobile")} data-testid="contact-mobile" className={inputCls} placeholder="+65 0000 0000" />
                        {errors.mobile && <p className="mt-1.5 text-xs text-red-500">{errors.mobile.message}</p>}
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Email *</label>
                        <input {...register("email")} data-testid="contact-email-input" className={inputCls} placeholder="you@company.com" />
                        {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>}
                      </div>
                    </div>

                    <div className="mt-5">
                      <label className="mb-1.5 block text-xs font-semibold text-navy">Service Interested In *</label>
                      <select {...register("service")} data-testid="contact-service" className={inputCls} defaultValue="">
                        <option value="" disabled>Select a service</option>
                        {SERVICES.map((s) => (
                          <option key={s.slug} value={s.title}>{s.title}</option>
                        ))}
                        <option value="General Enquiry">General Enquiry</option>
                      </select>
                      {errors.service && <p className="mt-1.5 text-xs text-red-500">{errors.service.message}</p>}
                    </div>

                    <div className="mt-5">
                      <label className="mb-1.5 block text-xs font-semibold text-navy">Message *</label>
                      <textarea {...register("message")} data-testid="contact-message" rows={4} className={inputCls} placeholder="How can we help your organisation?" />
                      {errors.message && <p className="mt-1.5 text-xs text-red-500">{errors.message.message}</p>}
                    </div>

                    <div className="mt-5 flex items-start gap-3">
                      <input
                        id="consent"
                        type="checkbox"
                        {...register("consent")}
                        data-testid="contact-consent"
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand focus:ring-brand"
                      />
                      <label htmlFor="consent" className="text-xs leading-relaxed text-slate-600">
                        I consent to SKY Training &amp; Consultancy collecting and using the information provided
                        to respond to my enquiry.
                      </label>
                    </div>
                    {errors.consent && <p className="mt-1.5 text-xs text-red-500">{errors.consent.message}</p>}

                    {submit.isError && (
                      <div
                        role="alert"
                        data-testid="contact-submit-error"
                        className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4"
                      >
                        <p className="text-sm font-semibold text-red-700">
                          Your enquiry could not be sent.
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-red-600">
                          Something went wrong on our side — your details are still here, so you can
                          try again. If it keeps failing, please{" "}
                          <a
                            href={waLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold underline"
                          >
                            message us on WhatsApp
                          </a>{" "}
                          or call {site.mobile}.
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submit.isPending || !consent}
                      data-testid="contact-form-submit-btn"
                      className="btn-primary-sky mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submit.isPending ? "Sending..." : (<>Send Enquiry <Send className="h-4 w-4" /></>)}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
